import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Select } from '@/nova/components';
import { useClipboard } from '@/nova/components/CopyButton';

import type { PlayableEmbedResult } from '../../playable-api';
import * as S from './PlayableComposer.styles';

/** An embed published this session: the format it serves and what the backend returned. */
export interface PublishedEmbed {
  format: string;
  result: PlayableEmbedResult;
}

export interface EmbedPanelProps {
  /** The mini-game's formats (one embed serves one of them). */
  formats: string[];
  /** A generated version is selected and nothing is running, so there is something to publish. */
  ready: boolean;
  /** An embed is being published. */
  busy: boolean;
  /** Another package of this version (an export) is being prepared: Publish waits for it. */
  blocked?: boolean;
  /** The embed published from the selected version this session, if any. */
  published: PublishedEmbed | null;
  /** Why the last publish failed (already translated), if it did. */
  error: string | null;
  onPublish: (format: string) => void;
}

const formatLabel = (format: string, responsive: string): string =>
  format === 'responsive' ? responsive : format.replace('x', '×');

/**
 * The mini-game's hosted embed: publish the selected version in one format to the sandboxed embed domain, then
 * copy the iframe snippet (or the iframe's URL) into any page. The embed reports its plays, reveals and clicks
 * back, and they show on the mini-game's card. Shown only where the backend has hosted embeds configured.
 */
export const EmbedPanel = ({
  formats,
  ready,
  busy,
  blocked = false,
  published,
  error,
  onPublish,
}: EmbedPanelProps): React.ReactElement => {
  const { t } = useTranslation();
  const [picked, setPicked] = React.useState('');
  const format = formats.includes(picked) ? picked : formats[0] ?? '';
  const snippetClip = useClipboard();
  const urlClip = useClipboard();
  const responsive = t('Responsive');

  const publish = (): void => {
    if (!ready || busy || blocked || !format) return; // self-gate: disabled styling alone is not a guard
    onPublish(format);
  };

  return (
    <S.ExportPanel aria-label={t('Hosted embed')}>
      <S.SectionTitle text="Hosted embed" />
      <S.Hint text="Publish this version as an iframe you can paste into any page. It is served from a separate, sandboxed domain, and its plays, reveals and clicks show on the mini-game's card." />
      <S.EmbedControls>
        <Select
          label={t('Format')}
          value={format}
          onChange={(f) => {
            if (!busy) setPicked(f);
          }}
          options={formats.map((f) => ({ value: f, label: formatLabel(f, responsive) }))}
          disabled={busy}
        />
        <Button
          variant="outline"
          icon="globe"
          label={busy ? t('Publishing…') : t('Publish embed')}
          disabled={!ready || busy || blocked || !format}
          onClick={publish}
        />
      </S.EmbedControls>
      {!ready ? <S.Hint text="Generate the art first. The embed publishes the selected version." /> : null}
      {error ? <S.EmbedError raw text={error} /> : null}

      {published ? (
        <>
          <S.FileHead>
            <S.FileMeta
              text="Embed code · {{format}}"
              placeholders={{ format: formatLabel(published.format, responsive) }}
            />
            <Button
              variant="text"
              icon={snippetClip.copied ? 'check' : 'copy'}
              label={snippetClip.copied ? t('Copied') : t('Copy code')}
              onClick={() => void snippetClip.copy(published.result.snippet)}
            />
          </S.FileHead>
          <S.EmbedCode
            readOnly
            value={published.result.snippet}
            aria-label={t('Embed code')}
            spellCheck={false}
            onFocus={(e) => e.currentTarget.select()}
          />
          {published.result.url ? (
            <S.CopyField>
              <S.CopyUrl
                readOnly
                value={published.result.url}
                aria-label={t('Embed URL')}
                onFocus={(e) => e.currentTarget.select()}
              />
              <S.CopyBtn
                type="button"
                $copied={urlClip.copied}
                onClick={() => void urlClip.copy(published.result.url)}
                aria-label={urlClip.copied ? t('Copied') : t('Copy link')}
                title={urlClip.copied ? t('Copied') : t('Copy link')}
              >
                <Icon name={urlClip.copied ? 'check' : 'copy'} size={16} />
              </S.CopyBtn>
            </S.CopyField>
          ) : null}
        </>
      ) : null}
    </S.ExportPanel>
  );
};

export default EmbedPanel;
